import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

import { setDeviceId } from '@/utils/deviceId';

import type { AuthState, User, LoginResponse, BruteForceState } from '../types/auth.types';
import { createInitialAuthState } from '../utils/hydrateAuthState';
import { clearSessionProbeFlag, setSessionProbeFlag } from '../utils/sessionProbe';
import { setTokens, clearTokens, setLocalUser, clearLocalUser } from '../utils/token';

const initialBruteForceState: BruteForceState = {
  failedAttempts: 0,
  lastFailedAttempt: null,
  isLocked: false,
  lockUntil: null,
};

const initialState: AuthState = createInitialAuthState();

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials(state, action: PayloadAction<LoginResponse>) {
      const { user, accessToken, refreshToken, deviceId } = action.payload;
      state.user = user;
      state.token = accessToken;
      state.refreshToken = refreshToken;
      state.isAuthenticated = true;
      state.isLoading = false;
      state.bruteForce = initialBruteForceState;
      setDeviceId(deviceId);
      setTokens(accessToken, refreshToken);
      setLocalUser(user);
      setSessionProbeFlag();
    },
    setUser(state, action: PayloadAction<User>) {
      state.user = action.payload;
      setLocalUser(action.payload);
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    setBruteForce(state, action: PayloadAction<BruteForceState>) {
      state.bruteForce = action.payload;
    },
    updateBruteForce(state, action: PayloadAction<Partial<BruteForceState>>) {
      state.bruteForce = { ...state.bruteForce, ...action.payload };
    },
    resetBruteForce(state) {
      state.bruteForce = initialBruteForceState;
    },
    logout(state) {
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.isLoading = false;
      state.bruteForce = initialBruteForceState;
      clearTokens();
      clearLocalUser();
      clearSessionProbeFlag();
    },
  },
});

export const {
  setCredentials,
  setUser,
  setLoading,
  setBruteForce,
  updateBruteForce,
  resetBruteForce,
  logout,
} = authSlice.actions;
export default authSlice.reducer;
